import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  alpha,
  Dialog,
  DialogContent,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { PlayArrow, EmojiEvents, Celebration, Casino, Store as StoreIcon, ConfirmationNumber } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { campaignService } from '../services/campaignService';
import { storeDrawService, StoreDraw } from '../services/storeDrawService';
import { Campaign } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '@mui/material/styles';
import { AppHeader } from '../components/AppHeader';
import { useToast } from '../contexts/ToastContext';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// 🎨 Animações personalizadas
const pulseGlow = keyframes`
  0% { 
    box-shadow: 0 0 20px rgba(0, 153, 41, 0.5);
    transform: scale(1);
  }
  50% { 
    box-shadow: 0 0 60px rgba(0, 153, 41, 0.9), 0 0 100px rgba(92, 203, 95, 0.6);
    transform: scale(1.02);
  }
  100% { 
    box-shadow: 0 0 20px rgba(0, 153, 41, 0.5);
    transform: scale(1);
  }
`;

const winnerReveal = keyframes`
  0% { 
    transform: scale(0.5) rotate(-10deg); 
    opacity: 0; 
  }
  60% { 
    transform: scale(1.1) rotate(5deg); 
    opacity: 1; 
  }
  100% { 
    transform: scale(1) rotate(0deg); 
    opacity: 1; 
  }
`;

const confettiAnimation = keyframes`
  0% { 
    transform: translateY(-100%) rotate(0deg);
    opacity: 1;
  }
  100% { 
    transform: translateY(100vh) rotate(720deg);
    opacity: 0;
  }
`;

const numberShuffle = keyframes`
  0%, 100% { transform: translateY(0); opacity: 1; }
  50% { transform: translateY(-20px); opacity: 0.5; }
`;

const diceRoll = keyframes`
  0% { transform: rotate(0deg) scale(1); }
  25% { transform: rotate(90deg) scale(1.1); }
  50% { transform: rotate(180deg) scale(1); }
  75% { transform: rotate(270deg) scale(1.1); }
  100% { transform: rotate(360deg) scale(1); }
`;

const ticketFloat = keyframes`
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
`;

const storeShine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// 🎊 Componente de Confetes
const Confetti: React.FC = () => {
  const confettiColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'];
  
  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999 }}>
      {[...Array(50)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            left: `${Math.random() * 100}%`,
            top: '-10px',
            width: '10px',
            height: '10px',
            backgroundColor: confettiColors[Math.floor(Math.random() * confettiColors.length)],
            animation: `${confettiAnimation} ${2 + Math.random() * 3}s linear infinite`,
            animationDelay: `${Math.random() * 2}s`,
            borderRadius: Math.random() > 0.5 ? '50%' : '0',
          }}
        />
      ))}
    </Box>
  );
};

interface StoreDrawFormData {
  campaignId: string;
  prizeDescription: string;
}

const schema = yup.object({
  campaignId: yup.string().required('Campanha é obrigatória'),
  prizeDescription: yup
    .string()
    .required('Descrição do prêmio é obrigatória')
    .max(255, 'Descrição deve ter no máximo 255 caracteres'),
});

const StoreDrawLive: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const { showError, showSuccess } = useToast();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [winnerStore, setWinnerStore] = useState<StoreDraw | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showWinner, setShowWinner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [suspenseStage, setSuspenseStage] = useState(0); // 0: preparando, 1: sorteando, 2: revelando
  const [randomNumber, setRandomNumber] = useState(0);
  const winnerSectionRef = React.useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<StoreDrawFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      campaignId: '',
      prizeDescription: '',
    },
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const data = await campaignService.getCampaignsByAssociation();
      setCampaigns(data);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      showError('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  };

  // 🎲 Animação de números aleatórios durante o sorteio
  useEffect(() => {
    if (isDrawing && (suspenseStage === 0 || suspenseStage === 1)) {
      const interval = setInterval(() => {
        setRandomNumber(Math.floor(Math.random() * 100000));
      }, 100); // Muda a cada 100ms para efeito rápido

      return () => clearInterval(interval);
    }
  }, [isDrawing, suspenseStage]);

  const performDraw = async (data: StoreDrawFormData) => {
    if (isDrawing) return;

    // 🔄 Limpar ganhador anterior ao sortear novamente
    setShowWinner(false);
    setWinnerStore(null);
    setShowConfetti(false);
    
    setIsDrawing(true);
    setSuspenseStage(0);

    try {
      console.log('🎲 Iniciando sorteio de loja...');
      
      // 🎬 FASE 1: Preparando (2s)
      setSuspenseStage(0);
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // 🎬 FASE 2: Sorteando com números embaralhando (3s)
      setSuspenseStage(1);
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // 🎬 FASE 3: Executar sorteio no backend
      console.log('🔄 Executando sorteio de loja no backend...');
      const result = await storeDrawService.performStoreDraw(data);
      console.log('✅ Sorteio de loja realizado:', result);
      
      if (result && result.winnerStore) {
        // 🎬 FASE 4: Revelando (2s)
        setSuspenseStage(2);
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // 🎊 Mostrar loja vencedora
        setWinnerStore(result);
        setShowWinner(true);
        setShowConfetti(true);
        
        // Mostrar mensagem de sucesso
        showSuccess(`🎉 Loja sorteada com sucesso: ${result.winnerStore.name}!`);
        
        // 📜 Scroll automático suave para a seção de ganhador após 500ms
        setTimeout(() => {
          if (winnerSectionRef.current) {
            winnerSectionRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 500);
      } else {
        console.warn('⚠️ Nenhuma loja vencedora retornada');
        showError('Erro ao sortear loja. Tente novamente.');
      }
    } catch (error: any) {
      console.error('❌ Erro ao realizar sorteio de loja:', error);
      
      // Extrair mensagem de erro amigável
      let errorMessage = 'Erro ao realizar sorteio de loja. Tente novamente.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showError(`❌ ${errorMessage}`);
    } finally {
      setIsDrawing(false);
      setSuspenseStage(0);
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
        }}
      >
        <Stack alignItems="center" spacing={3}>
          <CircularProgress 
            size={80} 
            thickness={4}
            sx={{ color: theme.palette.primary.main }} 
          />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            🎲 Preparando sorteio de loja...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <AppHeader 
        title="Sorteio de Loja ao Vivo" 
        subtitle="Sorteie uma loja participante da campanha"
        icon={<StoreIcon />}
        backTo="/store-draws"
      />

      {/* 🎊 Confetes */}
      {showConfetti && <Confetti />}

      {/* 🎬 Modal de Sorteio com Suspense */}
      <Dialog 
        open={isDrawing && !showWinner} 
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #009929 0%, #00b33c 100%)',
            boxShadow: '0 20px 60px rgba(0, 153, 41, 0.5)'
          }
        }}
      >
        <DialogContent sx={{ p: 6 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
              color: 'white'
            }}
          >
            {/* FASE 0: Preparando */}
            {suspenseStage === 0 && (
              <Stack alignItems="center" spacing={4}>
                <Casino sx={{ fontSize: 120, animation: `${diceRoll} 1s infinite`, color: 'white' }} />
                <Box sx={{ 
                  fontSize: '100px', 
                  fontWeight: 900,
                  animation: `${numberShuffle} 0.2s infinite`,
                  fontFamily: 'monospace',
                  color: 'rgba(255, 255, 255, 0.9)',
                  textShadow: '0 0 20px rgba(255, 255, 255, 0.5)'
                }}>
                  {randomNumber.toString().padStart(5, '0')}
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  🎲 Preparando o Sorteio...
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, textAlign: 'center' }}>
                  Gerando números aleatórios...
                </Typography>
              </Stack>
            )}

            {/* FASE 1: Sorteando */}
            {suspenseStage === 1 && (
              <Stack alignItems="center" spacing={4}>
                <Casino sx={{ fontSize: 150, animation: `${diceRoll} 0.5s infinite`, color: '#FFD700' }} />
                <Box sx={{ 
                  fontSize: '150px', 
                  fontWeight: 900,
                  animation: `${numberShuffle} 0.15s infinite`,
                  fontFamily: 'monospace',
                  color: 'rgba(255, 255, 255, 1)',
                  textShadow: '0 0 30px rgba(255, 215, 0, 0.8), 0 0 60px rgba(255, 215, 0, 0.5)'
                }}>
                  {randomNumber.toString().padStart(5, '0')}
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  🎰 Sorteando Loja...
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Aguarde enquanto selecionamos a loja vencedora!
                </Typography>
              </Stack>
            )}

            {/* FASE 2: Revelando */}
            {suspenseStage === 2 && (
              <Stack alignItems="center" spacing={4}>
                <Celebration sx={{ fontSize: 120, color: '#FFD700' }} />
                <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  🎉 Temos uma Loja Vencedora!
                </Typography>
                <CircularProgress size={60} sx={{ color: 'white' }} />
              </Stack>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
          minHeight: 'calc(100vh - 80px)',
          paddingTop: 4,
          paddingBottom: 4
        }}
      >
        <Container maxWidth="lg">
          {/* 📊 Formulário de Sorteio */}
          {!showWinner && (
            <Paper
              elevation={3}
              sx={{
                p: 4,
                mb: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <EmojiEvents sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main' }}
                >
                  Realizar Sorteio de Loja
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Selecione a campanha e descreva o prêmio que será sorteado entre as lojas participantes
                </Typography>
              </Box>

              <form onSubmit={handleSubmit(performDraw)}>
                <Stack spacing={3}>
                  <Controller
                    name="campaignId"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.campaignId}>
                        <InputLabel>Campanha *</InputLabel>
                        <Select {...field} label="Campanha *" disabled={isDrawing}>
                          {campaigns.map((campaign) => (
                            <MenuItem key={campaign.id} value={campaign.id}>
                              {campaign.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.campaignId && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 0.5 }}
                          >
                            {errors.campaignId.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="prizeDescription"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Descrição do Prêmio *"
                        placeholder="Ex: Viagem para Gramado, Bônus de R$ 5.000, etc"
                        error={!!errors.prizeDescription}
                        helperText={errors.prizeDescription?.message}
                        multiline
                        rows={3}
                        disabled={isDrawing}
                      />
                    )}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<PlayArrow />}
                      disabled={isDrawing}
                      sx={{
                        py: 2,
                        px: 6,
                        fontSize: '1.3rem',
                        fontWeight: 700,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #009929 0%, #00b33c 100%)',
                        boxShadow: '0 8px 24px rgba(0, 153, 41, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #007a1f 0%, #009929 100%)',
                          boxShadow: '0 12px 32px rgba(0, 153, 41, 0.6)',
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {isDrawing ? '🎲 Sorteando...' : '🎯 Sortear Loja Vencedora'}
                    </Button>
                  </Box>
                </Stack>
              </form>
            </Paper>
          )}

          {/* 🏆 Card da Loja Vencedora */}
          {showWinner && winnerStore && (
            <Card 
              ref={winnerSectionRef}
              elevation={6}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #ffffff 0%, #f0f9f4 100%)',
                border: `3px solid ${theme.palette.primary.main}`,
                animation: `${pulseGlow} 2s infinite`,
                mb: 4,
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #009929 0%, #00b33c 100%)',
                  py: 3,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                  🏆 LOJA VENCEDORA
                </Typography>
              </Box>

              <CardContent sx={{ p: 5 }}>
                <Stack spacing={4} alignItems="center">
                  {/* Nome da Loja Vencedora */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h2" sx={{ 
                      fontWeight: 900,
                      color: theme.palette.primary.main,
                      mb: 2,
                      animation: `${winnerReveal} 0.8s ease-out`
                    }}>
                      {winnerStore.winnerStore.name}
                    </Typography>
                  </Box>

                  {/* Card de Prêmio - Estilo Bilhete Premiado */}
                  <Paper
                    elevation={6}
                    sx={{
                      position: 'relative',
                      p: 4,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                      textAlign: 'center',
                      minWidth: 350,
                      border: '4px dashed rgba(0, 0, 0, 0.2)',
                      animation: `${ticketFloat} 3s ease-in-out infinite`,
                      boxShadow: '0 10px 40px rgba(255, 215, 0, 0.5)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -2,
                        left: -2,
                        right: -2,
                        bottom: -2,
                        background: 'linear-gradient(45deg, #FFD700, #FFA500, #FFD700)',
                        borderRadius: 3,
                        zIndex: -1,
                        opacity: 0.3,
                      }
                    }}
                  >
                    {/* Ícone de Ticket no Fundo */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        opacity: 0.15,
                        zIndex: 0
                      }}
                    >
                      <ConfirmationNumber 
                        sx={{ 
                          fontSize: 200,
                          color: '#000',
                          transform: 'rotate(-15deg)'
                        }} 
                      />
                    </Box>

                    {/* Conteúdo do Ticket */}
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 2 }}>
                        <EmojiEvents sx={{ fontSize: 32, color: '#000' }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#000', textTransform: 'uppercase', letterSpacing: 1 }}>
                          Prêmio
                        </Typography>
                      </Stack>
                      
                      <Typography variant="h3" sx={{ 
                        fontWeight: 900,
                        color: '#000',
                        animation: `${winnerReveal} 1s ease-out`,
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)',
                        mb: 2
                      }}>
                        {winnerStore.prizeDescription}
                      </Typography>

                      <Typography variant="caption" sx={{ 
                        color: '#000', 
                        fontWeight: 600,
                        opacity: 0.8,
                        textTransform: 'uppercase',
                        letterSpacing: 2
                      }}>
                        ★ Prêmio da Sorte ★
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Informações Adicionais */}
                  <Box sx={{ width: '100%', maxWidth: 600 }}>
                    <Stack spacing={2}>
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                          📅 Campanha: <span style={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                            {winnerStore.campaign.name}
                          </span>
                        </Typography>
                      </Paper>

                      {winnerStore.winnerStore.address && (
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                            📍 Endereço: <span style={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                              {winnerStore.winnerStore.address}
                            </span>
                          </Typography>
                        </Paper>
                      )}

                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                          📅 Data do Sorteio: <span style={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                            {new Date(winnerStore.drawDate).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </Typography>
                      </Paper>
                    </Stack>
                  </Box>

                  {/* Botões de Ação */}
                  <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/store-draws')}
                      sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                      }}
                    >
                      Ver Histórico de Sorteios
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowWinner(false);
                        setWinnerStore(null);
                        setShowConfetti(false);
                      }}
                      sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                      }}
                    >
                      Sortear Novamente
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
    </>
  );
};

export default StoreDrawLive;

